/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Images } from "@packages/sdk";
import { forwardRef, type JSX, useState } from "react";
import { useFocus, useHover, usePress } from "react-aria";

import type { ThemeType } from "../../../../../../global/emotion";
import { radius, spacing, stroke } from "../../../../../../global/emotion";
import { stylesOpacity } from "../../../../../../global/emotion/styles/stylesOpacity";
import { stylesOutline } from "../../../../../../global/emotion/styles/stylesOutline";
import { stylesScale } from "../../../../../../global/emotion/styles/stylesScale";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { KeyedElement } from "../../../../../types/keyedElement.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import type { CSSColorStrict } from "../../../../../utils/colors/types/CSSColorStrict.types";
import { determineChildrenInject } from "../../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../../utils/determineElementFromAsChild";
import { HallowImage } from "../../../../blocks/other/HallowImage/HallowImage";
import { Text } from "../../../_core/Text/Text.emotion";

const hallowElement: HallowElement = "div";

const determinedElementStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
  paddingRight: spacing.m,
  width: spacing.full,
};
const illustrationImgStyle: Interpolation = {
  WebkitUserDrag: "none",
  borderBottomLeftRadius: spacing.s,
  borderTopRightRadius: spacing.s,
  bottom: spacing.none,
  height: spacing.xl,
  left: spacing.none,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  position: "absolute",
  userSelect: "none",
  width: spacing.xl,
};
const imgStyle: Interpolation<{ color: CSSColorStrict }> = (props) => ({
  borderColor: props.color,
  borderRadius: radius.s,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  boxSizing: "border-box",
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
});
const imgContainerStyle: Interpolation = {
  position: "relative",
};
const infoContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.xxs,
  minWidth: spacing.none,
};
const infoTopContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
};
const metadataStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});
const rightActionsContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
};

const stylesSize: Record<
  `${"imgContainer" | "img"}${CampaignSmallProps["size"]}`,
  Interpolation
> = {
  imgContainers: {
    height: 72,
  },
  imgContainerm: {
    height: 80,
  },
  imgs: {
    height: 72,
    width: 72,
  },
  imgm: {
    height: 80,
    width: 80,
  },
};

export type CampaignSmallProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  activeState?: boolean;
  color: CSSColorStrict;
  description: string;
  focusState?: boolean;
  hoverState?: boolean;
  illustrationImgSrc: string;
  imgSrc: Images;
  metadata: string;
  rightActions?: KeyedElement | KeyedElement[];
  size?: "s" | "m";
  title: string;
};

export const CampaignSmall = forwardRef<any, CampaignSmallProps>(
  (
    {
      activeState = true,
      asChild = false,
      children,
      color,
      description,
      hoverState = true,
      focusState = true,
      illustrationImgSrc,
      imgSrc,
      metadata,
      rightActions,
      size = "m",
      title,
      ...props
    },
    ref,
  ) => {
    const [focus, setFocus] = useState(false);

    const { pressProps: activeProps } = usePress({});
    const { focusProps } = useFocus({
      onFocusChange: (focus) => setFocus(focus),
    });
    const { isHovered: hover, hoverProps } = useHover({});

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <div
          key="imgContainer"
          css={[imgContainerStyle, stylesSize[`imgContainer${size}`]]}
        >
          <HallowImage
            alt={title}
            src={imgSrc}
            size={"s"}
            css={[
              imgStyle({ color }),
              stylesOutline.base,
              stylesSize[`img${size}`],
              focus && focusState ? stylesOutline.focusControlled : null,
              hover && hoverState ? stylesOutline.hoverControlled : null,
            ]}
          />
          <img
            alt={title}
            src={illustrationImgSrc}
            css={illustrationImgStyle}
          />
        </div>,
        <div key="infoContainer" css={infoContainerStyle}>
          <div css={infoTopContainerStyle}>
            <Text
              overflow="ellipsis"
              size={size === "s" ? "m" : "l"}
              type="title"
            >
              {title}
            </Text>
            <Text overflow="ellipsis" size="s" type="body">
              {description}
            </Text>
          </div>
          <Text overflow="ellipsis" size="l" css={metadataStyle} type="detail">
            {metadata}
          </Text>
        </div>,
        rightActions ? (
          <div key="rightActions" css={rightActionsContainerStyle}>
            {rightActions}
          </div>
        ) : null,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        {...activeProps}
        {...focusProps}
        {...hoverProps}
        css={[
          determinedElementStyle,
          activeState ? stylesOpacity.active : null,
          activeState ? stylesOpacity.base : null,
          activeState && hoverState ? stylesScale.base : null,
          activeState && hoverState ? stylesScale.l : null,
        ]}
        {...props}
      >
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

CampaignSmall.displayName = "CampaignSmall";
